import { ChevronDownIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { PlaceResult } from 'src/features/business-create/components/bc-desired-location-selector'
import BusinessExactLocationSelector from 'src/features/business-create/components/bc-exact-location-selector'
import BCSpecificLocationItem from 'src/features/business-create/components/bc-specific-location-item'
import { useBusinessCreateContext } from 'src/features/business-create/context/business-create-context'
import { useBusinessCreateStore } from 'src/features/business-create/store/business-create-store'
import type { ExactLocation } from 'src/features/consumers/schemas/consumer'
import { cn } from 'src/shared/utils/cn'

const ROW_SCROLL_OFFSET = 294 // 270 row height + 24 gap

const BusinessCreateExactLocationsStep = () => {
  const listViewportRef = useRef<HTMLDivElement>(null)
  const { setFormData, formData } = useBusinessCreateStore()
  const { handleNext } = useBusinessCreateContext()
  const [selectedLocations, setSelectedLocations] = useState<(ExactLocation & { placeId: string })[]>([])
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setFormData({
      ...formData,
      exactLocations: selectedLocations.map((location) => ({
        latitude: location.latitude,
        longitude: location.longitude,
        propertyStreetAddress1: location.propertyStreetAddress1,
        propertyStreetAddress2: location.propertyStreetAddress2,
        propertyCity: location.propertyCity,
        propertyState: location.propertyState,
        propertyZip: location.propertyZip,
      })),
    })
    handleNext()
  }

  const onPlaceSelected = (place: PlaceResult) => {
    setSelectedLocations([
      ...selectedLocations,
      {
        latitude: place.latitude,
        longitude: place.longitude,
        propertyStreetAddress1: place.street,
        propertyCity: place.city,
        propertyState: place.state,
        propertyZip: place.zip,
        placeId: place.placeId,
      },
    ])
  }

  const updateScrollState = () => {
    if (!listViewportRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = listViewportRef.current
    setCanScrollUp(scrollTop > 2)
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 2)
  }

  useEffect(() => {
    updateScrollState()
  }, [selectedLocations])

  const scrollOneRowDown = () => {
    if (!listViewportRef.current) return
    listViewportRef.current.scrollBy({ top: ROW_SCROLL_OFFSET, behavior: 'smooth' })
  }

  const scrollOneRowUp = () => {
    if (!listViewportRef.current) return
    listViewportRef.current.scrollBy({ top: -ROW_SCROLL_OFFSET, behavior: 'smooth' })
  }

  return (
    <div
      className={cn(
        'relative flex size-full flex-row items-center justify-between gap-10 px-10',
        selectedLocations.length === 0 && 'justify-center',
      )}
    >
      <form id="business-create-form" className="flex flex-col gap-8" onSubmit={(e) => onSubmit(e)}>
        <h1 className="text-[36px] font-semibold leading-[125%] tracking-[-0.36px]">Enter specific location</h1>
        <BusinessExactLocationSelector
          placeholder={selectedLocations.length > 0 ? 'Add another property' : 'Enter property address'}
          onPlaceSelected={onPlaceSelected}
          selectedLocations={selectedLocations}
        />
      </form>
      {selectedLocations.length > 0 && (
        <div className="relative flex h-full flex-col items-center justify-center gap-4 pt-[100px]">
          <div className="relative flex h-full min-h-[260px] w-[640px] items-center justify-center">
            {canScrollUp && (
              <button
                type="button"
                onClick={scrollOneRowUp}
                className="absolute left-1/2 top-2 z-20 flex size-[50px] -translate-x-1/2 items-center justify-center rounded-full bg-accent text-primary shadow-[0_1px_20px_rgba(27,48,41,0.2)]"
              >
                <ChevronDownIcon className="size-6 rotate-180" />
              </button>
            )}
            <div
              ref={listViewportRef}
              onScroll={updateScrollState}
              className="max-h-[calc(100vh-40px-200px)] w-full overflow-y-auto px-6 py-10 [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex w-full flex-row flex-wrap items-start justify-center gap-6 transition-all duration-300">
                {selectedLocations.map((location) => (
                  <BCSpecificLocationItem
                    key={location.placeId}
                    location={location}
                    onRemoveLocation={() =>
                      setSelectedLocations(selectedLocations.filter((l) => l.placeId !== location.placeId))
                    }
                    variant={selectedLocations.length === 1 ? 'large' : 'default'}
                    className={selectedLocations.length === 1 ? undefined : 'w-[270px]'}
                  />
                ))}
              </div>
            </div>
          </div>

          {canScrollDown && (
            <button
              type="button"
              onClick={scrollOneRowDown}
              className="absolute bottom-2 left-1/2 z-20 flex size-[50px] -translate-x-1/2 items-center justify-center rounded-full bg-accent text-primary shadow-[0_2px_20px_rgba(27,48,41,0.25)] transition-opacity"
            >
              <ChevronDownIcon className="size-6" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default BusinessCreateExactLocationsStep
